import isEqual from 'lodash/isEqual';
import { isRepresentableConfig } from '@/utils/mapperConfigDraft';
import { parseMapperConfig, stringifyMapperConfig } from '@/utils/mapperConfigText';
import { mapperConfigErrorMessage } from '@/utils/mapperConfigValidation';
import MapperConfigBuilder from './mapperBuilder/MapperConfigBuilder.vue';
const EDITOR_MODE_STORAGE_KEY = 'mqttNgMapperConfigEditorMode';
const EDITOR_VIEW_STORAGE_KEY = 'mqttNgMapperConfigEditorView';

// A bridge can hold several topic mappers, and Monaco rejects a second model for a URI
// it already knows, so every editor instance gets its own.
let modelCounter = 0;
function readStoredValue(key) {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(key) : null;
  } catch (_) {
    return null;
  }
}
function writeStoredValue(key, value) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch (_) {
    // The choice still applies in memory when browser storage is unavailable.
  }
}
function initialMode() {
  return readStoredValue(EDITOR_MODE_STORAGE_KEY) === 'yaml' ? 'yaml' : 'json';
}
function initialView() {
  return readStoredValue(EDITOR_VIEW_STORAGE_KEY) === 'code' ? 'code' : 'builder';
}
export default {
  name: 'MapperConfigEditor',
  components: {
    MapperConfigBuilder
  },
  props: {
    kind: {
      type: String,
      default: 'packet',
      validator: value => ['packet', 'output'].includes(value)
    },
    value: {
      type: Object,
      required: false,
      default: undefined
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    const mode = initialMode();
    return {
      mode,
      view: initialView(),
      text: stringifyMapperConfig(this.value, mode),
      parseError: '',
      monaco: null,
      editor: null,
      mapperModelUri: null,
      mapperModel: null,
      monacoReady: false,
      monacoFailed: false,
      monacoError: ''
    };
  },
  computed: {
    representable() {
      return this.kind === 'packet' && isRepresentableConfig(this.value);
    },
    activeView() {
      // A config the builder cannot show stays editable as text instead of losing keys.
      return this.representable ? this.view : 'code';
    },
    activeTabIndex() {
      return this.activeView === 'code' ? 1 : 0;
    },
    semanticError() {
      if (this.value === undefined) {
        return '';
      }
      // Outgoing mappings use the code editor; their full DSL is validated by
      // the connector's standard validate command, not the incoming builder grammar.
      if (this.kind === 'output') {
        if (this.value.version !== 1) {
          return 'Unsupported version';
        }
        if (!Object.prototype.hasOwnProperty.call(this.value, 'topic')) {
          return 'Topic mapping is required';
        }
        if (!Object.prototype.hasOwnProperty.call(this.value, 'payload')) {
          return 'Payload mapping is required';
        }
        return '';
      }
      return mapperConfigErrorMessage(this.value);
    },
    statusError() {
      return this.parseError || this.semanticError;
    }
  },
  watch: {
    value(nextValue) {
      try {
        if (isEqual(parseMapperConfig(this.text, this.mode), nextValue)) {
          return;
        }
      } catch (_) {
        // Keep invalid in-progress edits untouched until the parent sends a real external value change.
      }
      const nextText = stringifyMapperConfig(nextValue, this.mode);
      if (nextText !== this.text) {
        this.text = nextText;
        this.editor && this.editor.setValue(nextText);
      }
    },
    disabled(value) {
      this.editor && this.editor.updateOptions({
        readOnly: value
      });
    }
  },
  mounted() {
    this.mountMonaco();
  },
  beforeDestroy() {
    this.editor && this.editor.dispose();
    this.mapperModel && this.mapperModel.dispose && this.mapperModel.dispose();
  },
  methods: {
    async mountMonaco() {
      try {
        this.monacoFailed = false;
        this.monacoError = '';
        const monaco = await import('monaco-editor/esm/vs/editor/editor.api');
        this.monaco = monaco;
        modelCounter += 1;
        const mapperModelUri = monaco.Uri && monaco.Uri.parse(`inmemory://model/mapper-config-${modelCounter}.json`);
        this.mapperModelUri = mapperModelUri;
        this.mapperModel = monaco.editor.createModel ? monaco.editor.createModel(this.text, this.mode === 'yaml' ? 'yaml' : 'json', mapperModelUri) : null;
        this.editor = monaco.editor.create(this.$refs.monacoHost, {
          value: this.mapperModel ? undefined : this.text,
          model: this.mapperModel || undefined,
          language: this.mode === 'yaml' ? 'yaml' : 'json',
          minimap: {
            enabled: false
          },
          automaticLayout: true,
          readOnly: this.disabled,
          scrollBeyondLastLine: false
        });
        this.editor.onDidChangeModelContent(() => {
          this.text = this.editor.getValue();
          this.emitParsedValue();
        });
        this.monacoReady = true;
      } catch (error) {
        this.monacoReady = false;
        this.monacoFailed = true;
        this.monacoError = error.message;
      }
    },
    setEditorText(text) {
      this.text = text;
      this.editor && this.editor.setValue(text);
    },
    switchView(nextView) {
      if (nextView !== 'builder' && nextView !== 'code') {
        return;
      }
      if (nextView === 'builder' && !this.representable) {
        return;
      }
      this.view = nextView;
      writeStoredValue(EDITOR_VIEW_STORAGE_KEY, nextView);
    },
    switchMode(nextMode) {
      if (nextMode !== 'json' && nextMode !== 'yaml' || nextMode === this.mode) {
        return;
      }
      let parsed;
      try {
        parsed = parseMapperConfig(this.text, this.mode);
      } catch (error) {
        this.parseError = `${this.$i18n_t('Bridges.mapperBuilder.code.cannotSwitchMode')} ${error.message}`;
        return;
      }
      this.mode = nextMode;
      writeStoredValue(EDITOR_MODE_STORAGE_KEY, nextMode);
      this.setEditorText(stringifyMapperConfig(parsed, nextMode));
      if (this.editor && this.monaco) {
        this.monaco.editor.setModelLanguage(this.editor.getModel(), nextMode === 'yaml' ? 'yaml' : 'json');
      }
      this.emitParsedValue();
    },
    formatCurrentText() {
      let parsed;
      try {
        parsed = parseMapperConfig(this.text, this.mode);
      } catch (error) {
        this.parseError = error.message;
        return;
      }
      this.setEditorText(stringifyMapperConfig(parsed, this.mode));
      this.emitParsedValue();
    },
    emitParsedValue() {
      let parsed;
      try {
        parsed = parseMapperConfig(this.text, this.mode);
      } catch (error) {
        this.parseError = error.message;
        return;
      }
      if (parsed === undefined) {
        this.parseError = '';
        this.$emit('input', undefined);
        return;
      }
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        this.parseError = this.$i18n_t('Bridges.mapperBuilder.code.mustBeObject');
        return;
      }
      this.parseError = '';
      this.$emit('input', parsed);
    }
  }
};